import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";

import { GridRowModes } from "@mui/x-data-grid";

import ADataGrid from "views/pages/manage/common/data-grid/ADataGrid";
import { AGridColDef } from "views/pages/manage/common/data-grid/ADataGridColDef";
import AButton from "views/pages/manage/common/form-fields-mui/AButton";
import AAutoComplete from "views/pages/manage/common/form-fields-mui/AAutoComplete";
import AInput from "views/pages/manage/common/formFeilds/customInput/AInput";
import CustomDatePicker from "views/pages/manage/common/commonComponents/CustomDatePicker";
import {
  failureToast,
  successToast,
} from "views/pages/manage/common/utils/methods/toasterFunctions/function";

import IconButtonWrapper from "views/pages/manage/common/form-fields-mui/IconButtonWrapper";

import { ReactComponent as DeleteIcon } from "assets/img/svg/Delete.svg";
import { ReactComponent as EditIcon } from "assets/img/svg/Edit2.svg";
import { ReactComponent as CrossIcon } from "assets/img/svg/CrossIcon.svg";

import { createTestMapping, updateTestMapping, getTermOptions } from "./helper";

const TestDetailsModal = ({
  open,
  courseId,
  defrId,
  onClose,
  closeWithFetch,
  closeWithoutDefrId,
}) => {
  const [termOptions, setTermOptions] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [rows, setRows] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const [rowForm, setRowForm] = useState({});
  const [editRowId, setEditRowId] = useState(null);
  const [showTable, setShowTable] = useState(false);

  useEffect(() => {
    const fetchTermOptions = async () => {
      const response = await getTermOptions();
      const dropdowns =
        response?.map((t) => ({
          label: t.termDispValue,
          value: t.id,
        })) || [];
      setTermOptions(dropdowns);
    };

    fetchTermOptions();
  }, []);

  // 🔄 Reset on open
  useEffect(() => {
    if (open) {
      setSelectedTerm(null);
      setRows([]);
      setRowForm({});
      setEditRowId(null);
      setRowModesModel({});
      setShowTable(false);
    }
  }, [open]);

  // ➕ Add Row
  const handleAddRow = () => {
    if (editRowId) return;

    const id = uuidv4();

    const newRow = {
      id,
      testId: "",
      testDescr: "",
      testDate: "",
      isNew: true,
    };

    setRows((prev) => [...prev, newRow]);
    setEditRowId(id);
    setRowForm(newRow);

    setRowModesModel((prev) => ({
      ...prev,
      [id]: { mode: GridRowModes.Edit, fieldToFocus: "testId" },
    }));
  };

  // ✏️ Edit
  const handleEditClick = (row) => {
    if (editRowId) return;

    setEditRowId(row.id);
    setRowForm(row);

    setRowModesModel((prev) => ({
      ...prev,
      [row.id]: { mode: GridRowModes.Edit, fieldToFocus: "testId" },
    }));
  };

  // Cancel
  const handleCancelClick = (id) => {
    const isNew = rows.find((r) => r.id === id)?.isNew;

    if (isNew) {
      setRows((prev) => prev.filter((r) => r.id !== id));
    }

    setEditRowId(null);
    setRowForm({});

    setRowModesModel((prev) => ({
      ...prev,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    }));
  };

  // 🗑 Delete
  const handleDeleteClick = async (id) => {
    const row = rows.find((r) => r.id === id);

    try {
      if (defrId) {
        const payload = {
          courseId,
          academicTermId: row.academicTermId,
          isDeleted: true,
        };

        await updateTestMapping({
          ...payload,
          id,
        });
      }

      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  // 🔄 Field Change
  const handleFieldChange = (key, value) => {
    setRowForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // ✅ Validation
  const validateRow = () => {
    if (!selectedTerm) return "Term required";
    if (!rowForm.testId) return "Test ID required";
    if (!rowForm.testDescr) return "Description required";
    if (!rowForm.testDate) return "Date required";

    const duplicate = rows.filter(
      (r) => r.testId === rowForm.testId && r.id !== editRowId,
    );
    if (duplicate.length > 0) return "Duplicate Test ID";

    return null;
  };

  // 💾 Save
  const handleSaveClick = async (id) => {
    const error = validateRow();
    if (error) return failureToast(error);

    const payload = {
      courseId,
      academicTermId: selectedTerm.value,
      academicTermDispValue: selectedTerm.label,
      testId: rowForm.testId,
      testDescr: rowForm.testDescr,
      testDate: rowForm.testDate,
    };

    try {
      let res;

      if (defrId && rowForm.isNew) {
        res = await createTestMapping(payload);

        if (res?.code != 200) return;
      } else if (defrId) {
        res = await updateTestMapping({
          ...payload,
          id: rowForm.id,
        });

        if (res?.code != 200) return;
      }

      let updated = [];
      if (!defrId) {
        updated = rows.map((r) =>
          r.id === id ? { id, ...payload, isNew: false } : r,
        );
      } else
        updated = rows.map((r) =>
          r.id === id ? { ...res.data.data[0], isNew: false } : r,
        );

      setRows(updated);
      setEditRowId(null);
      setRowForm({});

      setRowModesModel((prev) => ({
        ...prev,
        [id]: { mode: GridRowModes.View, ignoreModifications: true },
      }));
    } catch (err) {
      console.error(err);
    }
  };

  // Columns
  const columns = [
    new AGridColDef("serial", "S. No.").renderCellComponent(
      (params) => rows.findIndex((r) => r.id === params.row.id) + 1,
    ),

    new AGridColDef("testId", "Test ID")
      .editable(true)
      .renderCellComponent((p) => p.value || "-")
      .renderEditCellComponent(() => (
        <AInput
          value={rowForm.testId || ""}
          handler={(value) => handleFieldChange("testId", value)}
        />
      )),

    new AGridColDef("testDescr", "Description")
      .editable(true)
      .renderCellComponent((p) => p.value || "-")
      .renderEditCellComponent(() => (
        <AInput
          value={rowForm.testDescr || ""}
          handler={(value) => handleFieldChange("testDescr", value)}
        />
      )),

    new AGridColDef("testDate", "Test Date")
      .editable(true)
      .renderCellComponent((p) =>
        p.value ? new Date(p.value).toLocaleDateString() : "-",
      )
      .renderEditCellComponent(() => (
        <CustomDatePicker
          value={rowForm.testDate ? moment(rowForm.testDate) : null}
          handler={(index, date) =>
            handleFieldChange("testDate", new Date(date).toISOString())
          }
          minDate={new Date()}
        />
      )),

    new AGridColDef("actions", "Actions")
      .columnAlign("right")
      .renderCellComponent((params) => {
        const isEditing = editRowId === params.row.id;

        if (isEditing) {
          return (
            <>
              <AButton
                size="xs"
                onClick={() => handleSaveClick(params.row.id)}
                variant="primary_filled"
              >
                Save
              </AButton>

              <IconButtonWrapper
                onClick={() => handleCancelClick(params.row.id)}
              >
                <CrossIcon />
              </IconButtonWrapper>
            </>
          );
        }

        return (
          <>
            <IconButtonWrapper onClick={() => handleEditClick(params.row)}>
              <EditIcon />
            </IconButtonWrapper>

            <IconButtonWrapper onClick={() => handleDeleteClick(params.row.id)}>
              <DeleteIcon />
            </IconButtonWrapper>
          </>
        );
      }),
  ];

  const resetDialog = () => {
    setRows([]);
    setShowTable(false);
    setRowModesModel({});
    setEditRowId(null);
  };

  const handleClose = () => {
    if (defrId) closeWithFetch();
    else onClose();
  };

  const handleSaveMappings = () => {
    if (defrId) {
      closeWithFetch();
      successToast("Mappings created successfully.");
    } else closeWithoutDefrId(rows);
  };

  return (
    <Modal isOpen={open} size="lg">
      <ModalHeader toggle={handleClose}>
        Add Test Details
        <div className="text-xs mt-2">
          Select a term to create test mappings
        </div>
      </ModalHeader>

      <ModalBody>
        {/* TERM SELECT */}
        <div className="d-flex gap-2 mb-3">
          <div style={{ width: "50%" }}>
            <AAutoComplete
              items={termOptions}
              currentValue={selectedTerm}
              handleChange={(val) => {
                setSelectedTerm(val);
                resetDialog();
              }}
              isMulti={false}
              label="Term"
            />
          </div>

          <AButton
            onClick={() => setShowTable(true)}
            disabled={!selectedTerm}
            variant="primary_filled"
            size="xs"
            className="mt-auto"
          >
            Proceed
          </AButton>
        </div>

        {/* TABLE */}
        {showTable && (
          <>
            <h4 className="mt-3 mb-2">
              Create/View Mapping -Term {selectedTerm.label}
            </h4>
            <ADataGrid
              rows={rows}
              columns={columns}
              rowId={(row) => row?.id}
              rowModesModel={rowModesModel}
              height="200px"
            />

            <div className="d-flex justify-content-between mb-2">
              <AButton
                size="xs"
                onClick={handleAddRow}
                variant="outline"
                disabled={!!editRowId}
              >
                + Add Test
              </AButton>
            </div>

            <div className="d-flex w-100 justify-content-end mt-3">
              <AButton size="xs" onClick={handleClose} variant="outline">
                Close
              </AButton>

              <AButton
                size="xs"
                onClick={handleSaveMappings}
                variant="primary_filled"
                disabled={!rows.length || editRowId}
              >
                Save Mappings
              </AButton>
            </div>
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default TestDetailsModal;
